import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import { get } from 'src/services/httpClient.service';

const Package_Channel_Reconciliation = () => {
  const [allRows, setAllRows] = useState([]); // Full dataset
  const [rows, setRows] = useState([]);       // Filtered data
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [inputValue, setInputValue] = useState('');

  const navigate = useNavigate();

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'reconcileCode',
      headerName: 'Unique code',
      flex: 2,
      renderCell: (params) => (
        <span
          style={{ color: '#1976d2', cursor: 'pointer' }}
          onClick={() => navigate(`/OTT-Reconciliation/Package_Channel_Reconciliation_Data/${params.row.reconcileCode}`)}
        >
          {params.row.reconcileCode}
        </span>
      ),
    },
    { field: 'reconcileType', headerName: 'Reconcile Type', flex: 1 },
    { field: 'noOfProcess', headerName: 'No Of Process', flex: 1 },
    { field: 'remainingProcess', headerName: 'Remain Process', flex: 1 },
    //{ field: 'requestType', headerName: 'Request Type', flex: 1 },
    { field: 'crmMatchPercentage', headerName: 'CRM (%)', flex: 1 },
    { field: 'drmMatchPercentage', headerName: 'DRM (%)', flex: 1 },
    { field: 'createdDateStr', headerName: 'Added Date', flex: 1 },
    { field: 'createdBy', headerName: 'Generated At', flex: 1 },
    
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reconcile/type/PACKAGE_CHANNEL`
        );
        const reportData = response['data']? response['data'] : [];
        const dataWithId = reportData.map((item: any, index: number) => ({
          id: index + 1,
          createdDateStr: dayjs(item.createdDate).format('DD-MM-YYYY HH:mm:ss'),
          ...item,
        }));
        setAllRows(dataWithId);
        setRows(dataWithId); // Initialize table with all data
      } catch (error) {
        console.error('Error fetching reconcile data:', error);
        setAllRows([]);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = () => {
    const filtered = allRows.filter((row: any) =>
      row.unique_code?.toLowerCase().includes(inputValue.trim().toLowerCase())
    );
    setRows(filtered);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text('Manage Package / Channel Reconcile', 14, 15);

    if (inputValue)
      doc.text(`Filtered by: ${inputValue}`, 14, 22);

    const tableColumn = columns.map((col) => col.headerName);
    const tableRows = rows.map((row) =>
      columns.map((col) => row[col.field] ?? '')
    );

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save(`Reconcile_Report_${inputValue || 'All'}.pdf`);
  };

  const handleDownloadExcel = () => {
    try {
      const headers = columns.map((col) => col.headerName);
      const dataRows = rows.map((row) =>
        columns.map((col) => row[col.field] ?? '')
      );
      const excelData = [headers, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Reconcile Report');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `Reconcile_Report_${inputValue || 'All'}.xlsx`);
    } catch (error) {
      console.error('Excel generation failed:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Package Channel Reconciliation</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
          <PageTitle heading="Package Channel Reconciliation" subHeading="Package Channel Reconciliation" handleClick={undefined} /> </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
          { hasPermission(["OWNER"]) && (
            <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={handleDownloadExcel}>
              Download Report
            </Button>
          )}
          { hasPermission(["OWNER"]) && (
            <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={handleDownloadPDF}>
              Download PDF
            </Button>
          )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* Unique Code Search Filter */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Search by Unique Code"
            variant="outlined"
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  🔍
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Box>

      
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px',overflowX: 'auto'}}>
        
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              py: 1.5,
            },
          }}
        />
      </Box>
    </>
  );
};

export default Package_Channel_Reconciliation;
